// The PrimeNG theme preset handed to providePrimeNG in app.config.ts. Lived in app.module.ts until
// AB#23257 slice 14 replaced the module with bootstrapApplication; the tokens and their notes are unchanged.
import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';

// Lara's primary palette is emerald. Left alone, every primary-tinted control renders
// #10b981: checked checkboxes and radios, the selected datepicker day, and the focus
// border on every form field, since formField.focusBorderColor maps to {primary.color}.
//
// Values are custom properties rather than hex, per CODING-STANDARDS 3.2. They resolve
// to $iCentral-Primary in _icentral-mat-palettes.scss, so the PrimeNG theme and Angular
// Material cannot drift apart.
const IC_PRIMARY = {
    50: 'var(--ic-primary-50)',
    100: 'var(--ic-primary-100)',
    200: 'var(--ic-primary-200)',
    300: 'var(--ic-primary-300)',
    400: 'var(--ic-primary-400)',
    500: 'var(--ic-primary-500)',
    600: 'var(--ic-primary-600)',
    700: 'var(--ic-primary-700)',
    800: 'var(--ic-primary-800)',
    900: 'var(--ic-primary-900)',
    950: 'var(--ic-primary-950)',
};

// Token values measured from the Toro Lynx Component Library on
// feature/ng21-upgrade-primeNG (AB#23243). Every token name below was checked against
// the installed @primeuix/themes 3.0.0 before being lifted, because that branch is on
// PrimeNG 21 and this one is on 22.
//
// Note the controls that deliberately do not inherit the brand primary: the checkbox
// uses the informative blue and the toggle uses positive green, which is what Figma
// specifies, so both are set per component.
export const IcLara = definePreset(Lara, {
    semantic: {
        primary: IC_PRIMARY,
        colorScheme: {
            light: {
                primary: {
                    color: '{primary.500}',
                    contrastColor: 'var(--white)',
                    hoverColor: '{primary.600}',
                    activeColor: '{primary.700}',
                },
            },
        },
        // Figma "Text Field" and "Select Field", nodes 4233:34604 and 8455:94298.
        // Focus is a border change only, with no ring or shadow.
        formField: {
            background: 'var(--white)',
            disabledBackground: 'var(--ic-neutral-alpha-05)',
            color: 'var(--ic-gray-100)',
            disabledColor: 'var(--ic-neutral-alpha-40)',
            placeholderColor: 'var(--ic-gray-60)',
            borderColor: 'var(--ic-gray-30)',
            hoverBorderColor: 'var(--ic-gray-50)',
            focusBorderColor: 'var(--text-blue)',
            invalidBorderColor: 'var(--negative)',
            borderRadius: '4px',
            paddingX: '12px',
            paddingY: '6px',
        },
    },
    components: {
        // Figma "Checkbox", node 1823:10403: 18x18, informative blue fill, white
        // check. Sized through the token rather than a CSS rule because the v22 box
        // is a flex container and forcing its dimensions collapses the layout.
        checkbox: {
            root: {
                width: '18px',
                height: '18px',
                borderColor: 'var(--ic-gray-40)',
                checkedBackground: 'var(--text-blue)',
                checkedBorderColor: 'var(--border-blue)',
                checkedHoverBackground: 'var(--border-blue)',
                checkedHoverBorderColor: 'var(--border-blue)',
                checkedFocusBorderColor: 'var(--text-blue)',
            },
            icon: {
                checkedColor: 'var(--white)',
                checkedHoverColor: 'var(--white)',
            },
        },
        // Figma "Toggle", node 4233:34999: 38x20 track, positive green on,
        // Neutral/40 off. gap is px on purpose - Lara's 0.4rem is 6.4px at the 16px
        // root, which puts the knob off centre against the slider's 1px border.
        toggleswitch: {
            root: {
                width: '38px',
                height: '20px',
                background: 'var(--ic-gray-40)',
                checkedBackground: 'var(--positive)',
                checkedHoverBackground: 'var(--positive)',
                gap: '2px',
            },
        },
        // Figma "Slider", node 4247:34175: 1px Neutral/30 track, informative blue
        // range, 23x23 handle. The Water Budget control is its only use.
        slider: {
            track: {
                background: 'var(--ic-gray-30)',
                size: '1px',
            },
            range: {
                background: 'var(--text-blue)',
            },
            handle: {
                width: '23px',
                height: '23px',
                background: 'var(--text-blue)',
            },
        },
        // Figma draws the accordion panel as one white box, so the header and
        // content are transparent and the panel paints the background. Set as
        // tokens because PrimeNG's four header-background rules tie with ours on
        // specificity and win on document order.
        accordion: {
            header: {
                background: 'transparent',
                hoverBackground: 'transparent',
                activeBackground: 'transparent',
                activeHoverBackground: 'transparent',
            },
            content: {
                background: 'transparent',
            },
        },
    },
});
